/**
 * @fileoverview Owns the page-level "About this page" affordance on Practices,
 * Library, Notes, and Discovery: the info icon that hangs off the page title
 * (`renderPageInfoTitle`) and the right-panel body it opens
 * (`buildPageInfoPanel`).
 *
 * Extracted from three near-verbatim `showPageInfo()` bodies that had the same
 * tab-nav builder, tab-content builder, wrapper element, and tab-switch wiring,
 * and differed only in their title, intro sentence, tab data, and `contentType`.
 *
 * This module owns the DOM only. Each section keeps its own `PAGE_INFO_TABS`
 * data, its own `_panelOpen` bookkeeping, and its own `right-panel:open` emit —
 * because the `contentType` values are NOT derivable from the section
 * (`practice-info`, `library-info`, `discovery-info` — note Library's, which
 * matches neither its section name nor its route) and are load-bearing:
 * `PageManager._isPageInfoContentType` keys off the `-info` suffix to give the
 * active section a chance to clear its routed detail before the panel swaps.
 *
 * Deliberately NOT merged with `chat/helpHintPanel.js`. That renders the inline
 * card affordance ("What are experiments?"), which explains ONE CARD via an
 * annotated specimen; this explains a WHOLE PAGE via prose tabs. They answer
 * different questions at different altitudes, and folding them together buys a
 * shared shell at the cost of per-source block filtering.
 *
 * @module components/pageInfoPanel
 */

/**
 * Render a page title with the page-info icon attached to it.
 *
 * The icon is an absolutely positioned child of an inline-block wrapper around
 * the title text, so it sits outside the flow: the text stays optically centred
 * in the full-width `<h2>` and the page toolbar keeps its one-element-per-side
 * symmetry (menu left, close right). An earlier attempt put this button in the
 * toolbar's right corner next to the close X; two buttons on one side visibly
 * unbalanced the bar, and no amount of de-emphasis fixed a compositional
 * problem. Don't move it back there.
 *
 * Clicks are not bound here. PageManager delegates `[data-action="page-info"]`
 * from the page container to the active section's `showPageInfo()`, so this
 * survives the free-form re-rendering every list section does.
 *
 * `label` is a static string owned by the calling section — never user- or
 * model-derived — and is interpolated as-is, like the tab content below.
 *
 * @param {string} label - The page heading, e.g. "Library"
 * @returns {string} `<h2>` markup, ready to interpolate into a section template
 */
export function renderPageInfoTitle(label) {
  return `<h2 class="page-header__title content-width" data-sticky-slice><span class="page-header__title-text">${label}<button type="button" class="page-header__title-info" data-action="page-info" aria-label="About this page" data-tooltip="About this page" data-tooltip-position="right"><svg aria-hidden="true"><use href="#icon-page-info"/></svg></button></span></h2>`;
}

/**
 * Build the tabbed page-info panel body.
 *
 * The caller emits the panel, so this stays free of eventBus coupling and is
 * directly unit-testable.
 *
 * Tab content is author-controlled HTML held in each section's static
 * `PAGE_INFO_TABS`; it is assigned via `innerHTML` exactly as it was before
 * extraction. No caller passes user- or model-derived content, and none should.
 *
 * @param {Object} params
 * @param {string} params.title - Panel heading, e.g. "About Experiments"
 * @param {string} params.intro - One-paragraph lede under the heading
 * @param {Object<string, {label: string, content: string}>} params.tabs - Tab map
 * @param {string} [params.activeTab] - Key of the initially-open tab; defaults
 *   to the first key in `tabs`
 * @returns {HTMLElement} The panel body element, with tab switching already wired
 */
export function buildPageInfoPanel({ title, intro, tabs, activeTab }) {
  const tabKeys = Object.keys(tabs || {});
  const active = activeTab && tabKeys.includes(activeTab) ? activeTab : tabKeys[0];

  const tabNavHtml = tabKeys.map(key => {
    const isActive = key === active ? ' page-info-tab--active' : '';
    // data-info-label feeds the CSS ghost-bold width reservation (see
    // .page-info-tab::after) so the active-tab bolding never shifts layout.
    return `<button class="page-info-tab${isActive}" data-info-tab="${key}" data-info-label="${tabs[key].label}">${tabs[key].label}</button>`;
  }).join('');

  const tabContentHtml = tabKeys.map(key => {
    const hidden = key === active ? '' : ' style="display:none"';
    return `<div class="page-info-tab-content" data-info-content="${key}"${hidden}>${tabs[key].content}</div>`;
  }).join('');

  // A single tab is content, not navigation — skip the nav entirely so the
  // page reads as one unbroken explanation (e.g. /notes).
  const navHtml = tabKeys.length > 1
    ? `<nav class="page-info-tabs" role="tablist">${tabNavHtml}</nav>`
    : '';

  const content = document.createElement('div');
  content.className = 'page-info-detail content-width';
  content.innerHTML = `
      <h3 class="page-info-detail__title">${title}</h3>
      <p class="page-info-detail__text">
        ${intro}
      </p>
      ${navHtml}
      ${tabContentHtml}
    `;

  content.querySelectorAll('.page-info-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      const tab = btn.dataset.infoTab;
      content.querySelectorAll('.page-info-tab').forEach(b =>
        b.classList.toggle('page-info-tab--active', b.dataset.infoTab === tab)
      );
      content.querySelectorAll('.page-info-tab-content').forEach(c =>
        c.style.display = c.dataset.infoContent === tab ? '' : 'none'
      );
    });
  });

  // FAQ accordion toggle (shared .faq component; same behavior as the
  // page-level controllers on /upgrade, /partner, /subscription). Delegated,
  // because the panel mounts outside any section's container.
  content.addEventListener('click', (event) => {
    const question = event.target.closest('.faq__question');
    if (!question || !content.contains(question)) return;
    const item = question.closest('.faq__item');
    const icon = question.querySelector('.faq__icon');
    if (!item) return;
    const isOpen = item.classList.toggle('faq__item--open');
    if (icon) icon.textContent = isOpen ? '−' : '+';
  });

  return content;
}

/**
 * Render a "how it works" plate: a light-gray container holding a numbered
 * vertical step flow. Content strings are author-controlled static copy owned
 * by the calling section (same interpolation contract as tab content above).
 *
 * @param {Object} params
 * @param {string} [params.title='How it works'] - Uppercase plate eyebrow
 * @param {Array<{label: string, detail?: string}>} params.steps - Ordered steps
 * @returns {string} Plate markup, ready to embed in a tab's `content`
 */
export function renderPageInfoFlow({ title = 'How it works', steps }) {
  const stepsHtml = steps.map(({ label, detail }, idx) => `
    <li class="page-info-flow__step">
      <span class="page-info-flow__num" aria-hidden="true">${idx + 1}</span>
      <div>
        <span class="page-info-flow__label">${label}</span>
        ${detail ? `<span class="page-info-flow__detail">${detail}</span>` : ''}
      </div>
    </li>
  `).join('');
  return `
    <div class="page-info-plate">
      <h5 class="page-info-plate__title">${title}</h5>
      <ol class="page-info-flow">
        ${stepsHtml}
      </ol>
    </div>
  `;
}

/**
 * Render a "Common questions" accordion from Q/A pairs, using the shared
 * `.faq` component (components/faq.css). Toggle wiring lives in
 * buildPageInfoPanel's delegated listener, so this is markup-only and safe to
 * embed in any tab's `content`. Copy is author-controlled, as above.
 *
 * @param {Array<{q: string, a: string}>} items - Question/answer pairs; `a`
 *   may contain markup (it renders inside the answer slot, not a <p>)
 * @returns {string} FAQ markup, ready to embed in a tab's `content`
 */
export function renderPageInfoFaq(items) {
  const itemsHtml = items.map(({ q, a }, idx) => `
    <div class="faq__item">
      <button type="button" class="faq__question" data-faq="${idx}">
        <span>${q}</span>
        <span class="faq__icon">+</span>
      </button>
      <div class="faq__answer" data-faq-answer="${idx}">
        <p>${a}</p>
      </div>
    </div>
  `).join('');
  return `
    <div class="faq">
      <h3 class="faq__title">Common questions</h3>
      <div class="faq__list">
        ${itemsHtml}
      </div>
    </div>
  `;
}
